import { MouseEvent, useEffect, useState } from 'react'
import styles from './styles.module.scss'
import { Bottom, Fit, Top } from 'types/fit'
import useLocalStorage from '@helpers/hooks/useLocalStorage'
import { FilterOption } from 'types/filters'
import { BUILD } from '@consts/index'

type ToggleProps = {
  isToggled: boolean
  onToggle: (ev: MouseEvent<HTMLButtonElement>) => void
}

function Toggle({
  isToggled = false,

  onToggle
}: ToggleProps) {
  return (
    <button
      className={`${styles.Toggle} ${isToggled ? styles.on : styles.off}`}
      onClick={onToggle}
    >
      <span className={styles.Toggle__slider} />
    </button>
  )
}

type SavedFitBoxHeaderProps = ToggleProps & {
  onViewFitBox: () => void
  isFitOpen: boolean
}

const SavedFitBoxHeader = ({
  isToggled,
  onViewFitBox,
  onToggle,
  isFitOpen
}: SavedFitBoxHeaderProps) => {
  return (
    <div className={styles.SavedFitBoxHeader}>
      <div className={styles.SavedFitBoxHeader__toggle}>
        <Toggle isToggled={isToggled} onToggle={onToggle} />
        <p>{isToggled ? 'Your Fit' : 'Apply your Fit'}</p>
      </div>
      <button
        className={styles.SavedFitBoxHeader__button}
        onClick={onViewFitBox}
      >
        {isFitOpen ? 'Close' : 'View'}
      </button>
    </div>
  )
}

function getFitOptionsByName(names: (keyof Fit)[], savedFit: Partial<Fit>) {
  return names.map(option => {
    return {
      name: option,
      value: savedFit[option]
    }
  })
}

type SavedFitBoxSizeOptionsProps = {
  category:
    | {
        type: string
        options: {
          name: keyof Top | keyof Bottom
          value: string | undefined
        }[]
      }
    | undefined
}

const SavedFitBoxSizeOptions = ({ category }: SavedFitBoxSizeOptionsProps) => {
  const { type, options } = category || {}

  if (!options) return null

  return (
    <div className={styles.SavedFitBoxContent__categories}>
      <span className={styles.SavedFitBoxContent__title}>{type}:</span>
      <div className={styles.SavedFitBoxContent__options}>
        {options.map(option => (
          <div className={styles.SavedFitBoxContent__option} key={option.name}>
            <span className={styles.SavedFitBoxContent__optionType}>
              {option.name}
            </span>
            <span className={styles.SavedFitBoxContent__optionValue}>
              {option.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export const SavedFitBoxContent = ({
  savedFit
}: {
  savedFit: Partial<Fit>
}) => {
  const savedFitCategories = [
    {
      type: 'Bottoms',
      options: getFitOptionsByName(['waist', 'build', 'leg'], savedFit)
    },
    {
      type: 'Tops',
      options: getFitOptionsByName(['size', 'cut', 'length'], savedFit)
    }
  ]

  return (
    <div className={styles.SavedFitBoxContent}>
      {savedFitCategories.map(category => (
        <SavedFitBoxSizeOptions category={category} key={category.type} />
      ))}
    </div>
  )
}

type SavedFitBoxProps = {
  savedFit: Partial<Fit>
  onApplyFitToggle: (state: boolean) => void
  selectedOptions: FilterOption[]
}

const EXCLUDE_SELECTED_OPTIONS = ['shortsleg', 'swimshortsleg']

export function SavedFitBox({
  savedFit,
  selectedOptions,
  onApplyFitToggle
}: SavedFitBoxProps) {
  const [isSavedFitApplied, setIsSavedFitApplied] = useLocalStorage(
    'plp_filters_saved_fit_applied',
    false
  )
  const filteredSelectedOptions = selectedOptions.filter(
    option => !EXCLUDE_SELECTED_OPTIONS.includes(option.type)
  )

  const isSavedFitValid =
    filteredSelectedOptions.length > 0 &&
    filteredSelectedOptions.every(
      option =>
        savedFit[option.type as keyof Fit] ===
        String(option.value.replace(BUILD, '').trim())
    )

  const [isToggled, setIsToggled] = useState(isSavedFitApplied)
  const [isFitOpen, setIsFitOpen] = useState(false)

  const hasNoSavedFit = !Object.keys(savedFit).length

  const handleOnToggle = () => {
    onApplyFitToggle(!isToggled)
    setIsSavedFitApplied(!isToggled)
    setIsToggled(currentState => !currentState)
  }

  const handleOnViewFitBox = () => setIsFitOpen(prev => !prev)

  useEffect(() => {
    setIsToggled(isSavedFitValid)
    setIsFitOpen(isSavedFitApplied)
  }, [isSavedFitApplied, isSavedFitValid])

  if (hasNoSavedFit) return <></>

  return (
    <div
      className={`${styles.SavedFitBoxContainer} ${
        isToggled ? styles.SavedFitBoxContainer__on : ''
      }`}
    >
      <SavedFitBoxHeader
        onToggle={handleOnToggle}
        isToggled={isToggled}
        onViewFitBox={handleOnViewFitBox}
        isFitOpen={isFitOpen}
      />
      {isFitOpen ? <SavedFitBoxContent savedFit={savedFit} /> : null}
    </div>
  )
}

export default SavedFitBox
